import React, { useEffect, useState } from "react";
import "./addNewVisit.scss";
import FormInputWithHeader from "../../../../common/FormInputWithHeader";
import { NEW_VISIT_LIST } from "../../../ERXConstants/constants";
import RadioButtonWithText from "../../../../common/Modules/RadioButtonWithText";
import VitalComponent from "../../../../common/VitalComponent";
import moment from "moment";
import ErrorMsgPopup from "../../../common/ErrorMsgPopup";
import useDebounce from "../../../common/debounce";
import { ADD_EDIT_VITALS } from "../../../ERXViewApiHandler/apiConstants";
import { LOCALSTORAGEKEY } from "../../../../../Util/Helper/localStorageUtils";
import { c2cPostApiHandler } from "../../../../../Util/c2cApiHandler";

const AddNewVisit = (props) => {
  const {
    growthChartVitals,
    patientDetails,
    appointmentData,
    handleCloseVisit,
    handleOpenPrintChartPopup,
    fetchVitals,
    getVaccines,
    handleApiLoaders,
  } = props;

  const user_type = localStorage.getItem(LOCALSTORAGEKEY.USER_TYPE);
  const user_id = localStorage.getItem(LOCALSTORAGEKEY.USER_ID);

  const [newVisitList, setNewVisitList] = useState(
    JSON.parse(JSON.stringify(NEW_VISIT_LIST))
  );
  const [showError, setShowError] = useState(false);
  const [alertMsg, setAlertMsg] = useState("");
  const [callApis, setCallApis] = useState(false);

  const debouncedForVitals = useDebounce(newVisitList, 500);

  // Lifecycle Methods ===>

  useEffect(() => {
    handleGrowthChartVitals();
    setCallApis(false);
  }, [growthChartVitals]);

  useEffect(() => {
    if (callApis) {
      updateVitals();
    }
  }, [debouncedForVitals]);

  // <===

  // API Calls ===>

  const updateVitals = (close) => {
    let payload = {
      ...growthChartVitals,
      user_id: user_id,
      user_type: user_type,
    };
    newVisitList.forEach((ele) => {
      if (ele.name !== "dov" && ele.name !== "dob") {
        if (ele.name === "weight" || ele.name === "height") {
          let data = {
            [ele.name]: ele.value,
            [ele.name + "_unit"]: growthChartVitals[ele.name + "_unit"],
          };
          payload[ele.name] =
            ele.name === "weight" ? getWeight(data) : getHeight(data);
        } else {
          payload[ele.name] = ele.value;
        }
      }
    });
    handleApiLoaders("vitals", true);
    c2cPostApiHandler(ADD_EDIT_VITALS, payload).then(([res, error]) => {
      if (!error) {
        if (res?.data?.statusCode === 200) {
          fetchVitals();
          getVaccines();
          if (close === "close") {
            handleCloseVisit();
          }
        } else {
          handleApiLoaders("vitals", false);
        }
      } else {
        handleApiLoaders("vitals", false);
      }
    });
  };

  // <===

  // Helper Methods ===>

  const handleGrowthChartVitals = () => {
    let data = growthChartVitals ?? {};
    newVisitList.forEach((ele) => {
      ele.disabled =
        (data?.appointment_id ?? appointmentData?.appointment_id) !==
        appointmentData?.appointment_id;
      if (ele.name === "dov") {
        ele.value = data?.appointment_date
          ? moment(data?.appointment_date).format("DD/MM/YYYY")
          : moment(appointmentData?.appointment_date).format("DD/MM/YYYY");
        ele.disabled = true;
      }
      if (ele.name === "dob") {
        ele.title = (
          data?.patient_gender ? data?.patient_dob : patientDetails?.patient_dob
        )
          ? "Date of Birth"
          : "Patient Age";
        ele.value = getAge(data);
        ele.disabled = true;
      }
      if (ele.name === "weight") {
        ele.value = getWeight(data, "toKg");
      }
      if (ele.name === "height") {
        ele.value = getHeight(data, "toCm");
      }
      if (ele.name === "bmi") {
        ele.value = getBmi(data);
        ele.disabled = true;
      }
      if (
        ele.name === "head_circumference" ||
        ele.name === "muac" ||
        ele.name === "triceps"
      ) {
        ele.value = data[ele.name] ?? "";
        ele.hide = data.patientAgeInYears > 2;
      }
      if (ele.name === "oedema" || ele.name === "measured") {
        ele.value = data[ele.name] ?? "";
        ele.options.forEach((op) => {
          op.selected = data[ele.name] === op.value;
        });
      }
      if (ele.name === "notes") {
        ele.value = data[ele.name] ?? "";
        ele.inputType = window.innerWidth > 767 ? "normal" : "textarea";
      }
    });

    setNewVisitList([...newVisitList]);
  };

  const getAge = (data) => {
    let value = "";
    if (data?.patient_dob) {
      value = moment(data?.patient_dob).format("DD/MM/YYYY");
    } else if (data?.patient_age) {
      value = Number(data?.patient_age.split("years")[0].trim());
    }
    return value;
  };

  const getWeight = (data, toKg) => {
    let value = "";
    if (data.weight && data.weight_unit.toLowerCase() === "lbs") {
      value =
        toKg === "toKg"
          ? parseFloat(Number(data.weight) * 0.453592).toFixed(2)
          : parseFloat(Number(data.weight) / 0.453592).toFixed(2);
    } else {
      value = data.weight ?? "";
    }
    return value;
  };

  const getHeight = (data, toCm) => {
    let value = "";
    if (data.height && data.height_unit.toLowerCase() === "ft’in”") {
      value =
        toCm === "toCm"
          ? parseFloat(Number(data.height) * 30.48).toFixed(2)
          : parseFloat(Number(data.height) / 30.48).toFixed(2);
    } else {
      value = data.height ?? "";
    }
    return value;
  };

  const getBmi = (data) => {
    let value = "";
    if (data.height && data.weight) {
      let weight =
        data.weight_unit.toLowerCase() === "lbs"
          ? Number(data.weight) * 0.453592
          : Number(data.weight);
      let height =
        data.height_unit.toLowerCase() === "ft’in”"
          ? Number(data.height) * 0.3048
          : Number(data.height) / 100;
      value = parseFloat(weight / Math.pow(height, 2)).toFixed(2);
    }
    return value;
  };

  // <===

  // Actions ===>

  const handleOnChange = (item, val) => {
    setCallApis(true);
    let ind = newVisitList.findIndex((ele) => ele.name === item.name);
    if (
      newVisitList[ind].name === "weight" ||
      newVisitList[ind].name === "height"
    ) {
      newVisitList[ind].value = val.target.value;
      let weightInd = newVisitList.findIndex((ele) => ele.name === "weight");
      let heightInd = newVisitList.findIndex((ele) => ele.name === "height");
      let bmiInd = newVisitList.findIndex((ele) => ele.name === "bmi");
      let data = {
        weight: newVisitList[weightInd].value,
        weight_unit: newVisitList[weightInd].unit,
        height: newVisitList[heightInd].value,
        height_unit: newVisitList[heightInd].unit,
      };
      newVisitList[bmiInd].value = getBmi(data);
    } else if (
      newVisitList[ind].name === "oedema" ||
      newVisitList[ind].name === "measured"
    ) {
      newVisitList[ind].value = val.value;
      newVisitList[ind].options.forEach((ele) => {
        ele.selected = ele.value === val.value;
      });
    } else {
      let err = false;
      if (newVisitList[ind].name !== "notes") {
        err =
          (newVisitList[ind].min
            ? Number(val.target.value) < newVisitList[ind].min
            : false) ||
          (newVisitList[ind].max
            ? Number(val.target.value) > newVisitList[ind].max
            : false);
      } else if (newVisitList[ind].name === "notes") {
        err = val.target.value.length > newVisitList[ind].max;
      }

      if (!err) {
        newVisitList[ind].value = val.target.value;
      } else {
        setShowError(true);
        setAlertMsg(newVisitList[ind].errorMsg);
        setTimeout(() => {
          setShowError(false);
        }, 1500);
      }
    }

    setNewVisitList([...newVisitList]);
  };

  const handleSaveVisit = () => {
    if (
      (growthChartVitals?.appointment_id ?? appointmentData?.appointment_id) ===
      appointmentData?.appointment_id
    ) {
      updateVitals("close");
      handleOpenPrintChartPopup();
    }
  };

  // <===

  // Display UI ===>

  const displayNewVisitFields = () => {
    return (
      <div className="row" style={{ marginRight: "-5px" }}>
        {newVisitList.map(
          (item) =>
            !item.hide && (
              <div
                key={item.id}
                className={item.colSize}
                style={{ marginBottom: "15px", paddingRight: "5px" }}
              >
                {item.inputType === "normal" && (
                  <FormInputWithHeader
                    title={item.title}
                    type={item.type}
                    value={item.value}
                    disabled={item.disabled}
                    handleOnChangeInput={(e) => handleOnChange(item, e)}
                  />
                )}
                {item.inputType === "textarea" && (
                  <>
                    <div className="add-new-visit-field-header">
                      {item.title}
                    </div>
                    <textarea
                      value={item.value}
                      onChange={(e) => handleOnChange(item, e)}
                      disabled={item.disabled}
                      className="add-new-visit-textarea"
                    />
                  </>
                )}
                {item.inputType === "vital" && (
                  <>
                    <div className="add-new-visit-field-header">
                      {item.title}
                    </div>
                    <VitalComponent
                      item={item}
                      value={item.value}
                      disabled={item.disabled}
                      selectedUnit={item.unit}
                      unitList={item.unitList}
                      allowDecimal={true}
                      handleOnChange={(e) => handleOnChange(item, e)}
                    />
                  </>
                )}
                {item.inputType === "radio" && (
                  <>
                    <div className="add-new-visit-field-header">
                      {item.title}
                    </div>
                    <div className="add-new-visit-radio-input">
                      {item.options.map((ele, ind) => (
                        <div key={ind} className="add-new-visit-radio-values">
                          <RadioButtonWithText
                            data={ele}
                            text={ele.name}
                            value={ele.value}
                            disabled={item.disabled}
                            selected={ele.selected}
                            handleChange={(element) =>
                              handleOnChange(item, element)
                            }
                          />
                        </div>
                      ))}
                    </div>
                  </>
                )}
              </div>
            )
        )}
      </div>
    );
  };

  const displayNewVisitFooter = () => {
    return (
      <div className="add-new-visit-footer">
        <div
          className="add-new-visit-footer-cancel-btn"
          onClick={handleCloseVisit}
        >
          Cancel
        </div>
        <div
          className="add-new-visit-footer-save-btn"
          onClick={handleSaveVisit}
        >
          Save
        </div>
      </div>
    );
  };

  // <===

  return (
    <div className="add-new-visit-container">
      {displayNewVisitFields()}
      {displayNewVisitFooter()}
      {showError && <ErrorMsgPopup alertMsg={alertMsg} />}
    </div>
  );
};

export default AddNewVisit;
